import { IComputedParameterDef, IParameter, TParameterKey } from '@/pages/Parameters/parameters.types'
import { useMemo, useCallback } from 'react'
import dataUtils from '../data.utils'
import useDataUpToTsRef, { IUseDataUpToTsRef } from './useDataPointUpToTsRef'
import { IUseDataRefProps } from './useDataRef'

export interface IUseDataPointUpToTsWithComputedRefProps extends Omit<IUseDataRefProps, 'parameters'> {
  tsCurrent: number
  parameters: TParameterKey[]
  onNewValueReceived?: () => void
  computed?: IComputedParameterDef[]
}

const useDataPointUpToTsWithComputedRef = (props: IUseDataPointUpToTsWithComputedRefProps) => {
  const parsedToBaseParameterIds: IParameter['id'][] = useMemo(
    () => dataUtils.parsedToBaseParameterIds(props.parameters, props.computed),
    [props.parameters, props.computed]
  )

  const recompute = useCallback(() => {
    if (!props.computed?.length) return
    for (const e of props.devices) {
      if (!parametersData.ref.current[e]) continue
      for (const c of props.computed) {
        parametersData.ref.current[e][c.id] = c.compute({
          equipmentId: e,
          get: (id) => parametersData.ref.current[e][id],
        })
      }
    }
  }, [props.computed, props.devices])

  const handleNewValueReceived = useCallback(() => {
    recompute()
    if (props.onNewValueReceived) props.onNewValueReceived()
  }, [recompute, props.onNewValueReceived, props.computed])

  const dataConfig: Omit<IUseDataUpToTsRef, 'tsCurrent'> = useMemo(
    () => ({
      initialTsFrom: props.initialTsFrom,
      initialTsTo: props.initialTsTo,
      devices: props.devices,
      parameters: parsedToBaseParameterIds,
      aggregate: props.aggregate ?? false,
      live: false,
      onNewValueReceived: handleNewValueReceived,
    }),
    [props.initialTsFrom, props.initialTsTo, props.devices, parsedToBaseParameterIds, props.aggregate]
  )

  const parametersData = useDataUpToTsRef({ ...dataConfig, tsCurrent: props.tsCurrent })

  return {
    ref: parametersData.ref,
    loading: parametersData.loading,
  }
}

export default useDataPointUpToTsWithComputedRef
